// ─── KPI Cards ─────────────────────────────────────────────────────────────

/** Keys returned by /approval_analytics_dashboard/overview_cards/ that we display. */
export enum ApprovalAnalyticsKpiKey {
  Transactions = 'transactions', // Total transaction attempts (all types)
  Approvals = 'approvals',
  ApprovalRate = 'approval_rate',
  GrossRevenue = 'gross_revenue', // Approved Revenue
}

export interface ApprovalKpiCard {
  title: string;
  hint: string;
  value: string;
  trend_pct: number | null;
}

/** Default (empty) state — shown while loading or when there is no data. */
export const approvalKpiCards: ApprovalKpiCard[] = [
  {
    title: 'Total Attempts',
    hint: 'Total number of transaction attempts in the selected period.',
    value: '--',
    trend_pct: null,
  },
  {
    title: 'Total Approvals',
    hint: 'Total number of approved transactions in the selected period.',
    value: '--',
    trend_pct: null,
  },
  {
    title: 'Approval Rate',
    hint: 'Percentage of transaction attempts that were successfully approved.',
    value: '--',
    trend_pct: null,
  },
  {
    title: 'Approved Revenue',
    hint: 'Total gross revenue from approved transactions in the selected period.',
    value: '$0.00',
    trend_pct: null,
  },
];

// ─── Performance Breakdown ─────────────────────────────────────────────────

/** A single chip in the Performance Breakdown dimension selector. */
export interface ApprovalPerfChip {
  key: string;
  label: string;
  isDeferred: boolean;
  reason?: string;
}

export const dimensionStatusDeferred = 'deferred';

/**
 * Ordered whitelist of the dimension keys shown as chips in Performance Breakdown.
 * The API response may contain many more — we display only these, in this order.
 */
export const PerfChipKeys: string[] = [
  'product_name',
  'campaign_name',
  'gateway_name',
  'traffic_source',
  'issuer_bank_name',
  'price_point',
  'billing_cycle_group',
  'card_type',
  'day_of_week',
];

/**
 * Display label overrides — ensure chip labels match the Figma design
 * regardless of what the API returns for each dimension key.
 */
export const PerfChipAbbrLabels: { [key: string]: string } = {
  product_name: 'Products',
  campaign_name: 'Campaigns',
  gateway_name: 'Merchant/Acquirer',
  traffic_source: 'Traffic Sources',
  issuer_bank_name: 'Issuer Bank/Bin',
  price_point: 'Price Points',
  billing_cycle_group: 'Billing Cycles',
  card_type: 'Card Brands',
  day_of_week: 'Day of Week',
};

/** Display model for a table row — all values pre-formatted as strings. */
export interface ApprovalPerfRow {
  dimension: string;
  attempts: string;
  approvals: string;
  approvalRate: string;
  approvedRevenue: string;
  expanded?: boolean;
  loading?: boolean;
  children?: ApprovalPerfRow[];
}

// ─── Dimension Options ─────────────────────────────────────────────────────

export interface ApprovalDimension {
  key: string;
  label: string;
  status?: string;
  reason?: string;
}

export interface ApprovalDimensionGroup {
  key: string;
  label: string;
  dimensions: ApprovalDimension[];
}

export interface DimensionOptionsResponse {
  groups: ApprovalDimensionGroup[];
}

// ─── AI Summary ────────────────────────────────────────────────────────────

export interface AiInsight {
  text: string;
  icon: string;
}

export interface AiSummaryResponse {
  insights: AiInsight[];
  suppressed: any[];
  status: string;
}

/** Placeholder insights — shown while the real AI endpoint is not yet implemented. */
export const aiInsightsMock: AiInsight[] = [
  {
    text: 'Rebill approvals are 1.91% higher this month compared to last month.',
    icon: 'rebill-approval-icon.svg',
  },
  {
    text: 'Billing Cycle 2 has the highest approval rate at 93.12%.',
    icon: 'billing-cycle-icon.svg',
  },
  {
    text: 'Chase Bank has the lowest approval rate among top banks at 84.21%.',
    icon: 'chase-bank-icon.svg',
  },
  {
    text: 'Transactions between 10AM - 2PM have the highest approval rate.',
    icon: 'transactions-icon.svg',
  },
];
